import React from 'react';
import { Button } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import Swal from 'sweetalert2';
import logger from '../../utils/logger';

const PaymentReceipt = ({ payment, showLabel = false, className = "" }) => {
    /**
     * تحويل الأرقام إلى كلمات باللغة العربية
     * @param {number} number - الرقم المراد تحويله
     * @returns {string} الرقم مكتوبًا بالكلمات العربية
     */
    const convertNumberToArabicWords = (number) => {
        if (typeof number !== 'number') {
            return '';
        }
        
        // الأرقام الأساسية
        const ones = ['', 'واحد', 'اثنان', 'ثلاثة', 'أربعة', 'خمسة', 'ستة', 'سبعة', 'ثمانية', 'تسعة', 'عشرة', 
                    'أحد عشر', 'اثنا عشر', 'ثلاثة عشر', 'أربعة عشر', 'خمسة عشر', 'ستة عشر', 'سبعة عشر', 'ثمانية عشر', 'تسعة عشر'];
        
        // العشرات
        const tens = ['', 'عشرة', 'عشرون', 'ثلاثون', 'أربعون', 'خمسون', 'ستون', 'سبعون', 'ثمانون', 'تسعون'];
        
        // المئات
        const hundreds = ['', 'مائة', 'مئتان', 'ثلاثمائة', 'أربعمائة', 'خمسمائة', 'ستمائة', 'سبعمائة', 'ثمانمائة', 'تسعمائة'];
        
        // الآلاف والملايين
        const scales = ['', 'ألف', 'مليون', 'مليار', 'تريليون'];
        
        // تعامل خاص مع الصفر
        if (number === 0) {
            return 'صفر';
        }
        
        // تحويل الرقم إلى سلسلة نصية وإزالة النقطة العشرية
        let numStr = Math.floor(Math.abs(number)).toString();
        
        // تقسيم الرقم إلى مجموعات من 3 أرقام (آحاد، آلاف، ملايين، الخ)
        let groups = [];
        while (numStr.length) {
            groups.push(numStr.slice(-3));
            numStr = numStr.slice(0, -3);
        }
        
        // دالة لتحويل مجموعة من 3 أرقام إلى كلمات
        const convertGroup = (group) => {
            let result = '';
            let num = parseInt(group, 10);
            
            if (num === 0) {
                return '';
            }
            
            // المئات
            if (num >= 100) {
                result += hundreds[Math.floor(num / 100)];
                num %= 100;
                if (num > 0) {
                    result += ' و';
                }
            }
            
            // العشرات والآحاد
            if (num > 0) {
                if (num < 20) {
                    // الأرقام من 1 إلى 19
                    result += ones[num];
                } else {
                    // الأرقام من 20 فما فوق
                    const one = num % 10;
                    const ten = Math.floor(num / 10);
                    
                    if (one > 0) {
                        result += ones[one] + ' و';
                    }
                    
                    result += tens[ten];
                }
            }
            
            return result;
        };
        
        // تحويل كل مجموعة وإضافة المقاييس المناسبة (آلاف، ملايين، الخ)
        let result = '';
        for (let i = 0; i < groups.length; i++) {
            const groupText = convertGroup(groups[i]);
            
            if (groupText) {
                if (result) {
                    result = ' و' + result;
                }
                
                // إضافة المقياس المناسب (ألف، مليون، الخ)
                if (i > 0) {
                    result = groupText + ' ' + scales[i] + result;
                } else {
                    result = groupText + result;
                }
            }
        }
        
        // إضافة علامة السالب إذا كان الرقم سالبًا
        if (number < 0) {
            result = 'سالب ' + result;
        }
        
        return result;
    };

    const printReceipt = () => {
        try {
            // Format date properly
            const formattedDate = payment.date ? 
                new Date(payment.date).toLocaleDateString('ar-EG') : 
                new Date().toLocaleDateString('ar-EG');
                
            // Enhanced receipt content optimized for A4 paper in landscape mode with two receipts per page
            const receiptContent = `
                <!DOCTYPE html>
                <html dir="rtl" lang="ar">
                <head>
                    <meta charset="UTF-8">
                    <title>إيصال دفع - ${payment.id}</title>
                    <style>
                        @media print {
                            @page {
                                size: A4 landscape;
                                margin: 0;
                            }
                            * {
                                box-sizing: border-box;
                                text-align: right;
                            }
                            body { 
                                font-family: 'Tajawal', 'Cairo', 'Arial', sans-serif;
                                margin: 0;
                                padding: 0;
                                background-color: #fff;
                                color: #000;
                                height: 210mm;
                                width: 297mm;
                                direction: rtl;
                            }
                            .print-container { 
                                width: 297mm;
                                height: 210mm;
                                padding: 4mm;
                                position: relative;
                                display: flex;
                                flex-direction: row;
                                justify-content: space-between;
                            }
                            .receipt-wrapper {
                                width: 142.5mm;
                                height: 202mm;
                                display: flex;
                                flex-direction: column;
                            }
                            .receipt { 
                                border: 2px solid #000; 
                                border-radius: 8mm;
                                padding: 8mm;
                                height: 100%;
                                position: relative;
                                overflow: hidden;
                                font-size: 3.8mm;
                                display: flex;
                                flex-direction: column;
                                background-color: #fff;
                            }
                            .receipt::before {
                                content: "نسخة";
                                position: absolute;
                                top: 50%;
                                left: 50%;
                                transform: translate(-50%, -50%) rotate(-45deg);
                                color: rgba(0,0,0,0.05);
                                white-space: nowrap;
                                font-weight: bold;
                                z-index: 0;
                                font-size: 60px;
                            }
                            .header { 
                                text-align: center; 
                                margin-bottom: 4mm;
                                position: relative;
                                z-index: 1;
                            }
                            .logo { 
                                margin-bottom: 3mm;
                                font-weight: bold;
                                font-size: 7mm;
                                color: #000;
                                border-bottom: 1.5px solid #333;
                                padding-bottom: 2mm;
                                text-align: center;
                            }
                            .logo-subtitle {
                                font-size: 3.5mm;
                                margin-top: 1.5mm;
                                color: #666;
                                text-align: center;
                            }
                            .receipt-info {
                                display: flex;
                                justify-content: space-between;
                                margin-top: 4mm;
                                margin-bottom: 4mm;
                            }
                            .receipt-number-container {
                                text-align: right;
                            }
                            .date-container {
                                text-align: left;
                            }
                            .title { 
                                font-size: 6mm;
                                font-weight: bold;
                                margin: 3mm 0;
                                color: #000;
                                text-decoration: underline;
                                text-align: center;
                            }
                            .receipt-number-label, .date-label {
                                font-size: 3.2mm;
                                color: #666;
                                margin-bottom: 1.2mm;
                                text-align: right;
                            }
                            .receipt-number, .date-value {
                                font-size: 3.5mm;
                                background-color: #f8f8f8;
                                padding: 1.2mm 2.5mm;
                                font-weight: bold;
                                display: inline-block;
                                border: 1px solid #eaeaea;
                                border-radius: 1.5mm;
                            }
                            .details-container {
                                margin: 4mm 0;
                                position: relative;
                                z-index: 1;
                                flex: 1;
                            }
                            .details { 
                                border: 1.5px solid #eee;
                                border-radius: 3mm;
                                padding: 6mm;
                                background-color: #fdfdfd;
                                height: 100%;
                            }
                            .section-title {
                                font-size: 4mm;
                                font-weight: bold;
                                margin-bottom: 2.5mm;
                                border-bottom: 1.5px solid #eaeaea;
                                padding-bottom: 1.2mm;
                                color: #333;
                                text-align: right;
                            }
                            .detail-row { 
                                display: flex; 
                                flex-direction: row;
                                align-items: flex-start;
                                justify-content: flex-start;
                                margin: 2mm 0;
                                padding: 1.2mm 0;
                                border-bottom: 1px dashed #eaeaea;
                                text-align: right;
                            }
                            
                            .detail-label {
                                font-weight: bold;
                                color: #444;
                                font-size: 3.3mm;
                                text-align: right;
                                margin-left: 2mm;
                                white-space: nowrap;
                                min-width: 28mm;
                            }
                            
                            .detail-value {
                                color: #111;
                                font-size: 3.3mm;
                                text-align: right;
                                flex: 1;
                            }
                            
                            .description-value {
                                display: inline;
                                color: #111;
                                font-size: 3.3mm;
                                text-align: right;
                                word-wrap: break-word;
                                flex: 1;
                            }
                            .amount-section {
                                margin: 4mm 0;
                                background-color: #f9f9f9;
                                border-radius: 4mm;
                                padding: 6mm;
                                border: 1.5px dashed #ccc;
                                text-align: center;
                            }
                            .amount-label {
                                font-size: 4mm;
                                font-weight: bold;
                                margin-bottom: 2mm;
                                color: #555;
                                text-align: center;
                            }
                            .amount-value {
                                font-size: 6mm;
                                font-weight: bold;
                                color: #000;
                                margin-bottom: 2mm;
                                text-align: center;
                            }
                            .amount-in-words {
                                font-size: 3mm;
                                margin-top: 1.5mm;
                                color: #555;
                                padding: 1.5mm 2.5mm;
                                background-color: #fff;
                                border-radius: 2mm;
                                border: 1px solid #eee;
                                display: inline-block;
                                text-align: center;
                            }
                            .payment-type {
                                display: inline-block;
                                padding: 1.2mm 2.5mm;
                                font-size: 3mm;
                                border-radius: 2mm;
                                background-color: #f0f0f0;
                                font-weight: bold;
                            }
                            .case-type { background-color: #e3f2fd; color: #0d47a1; }
                            .consultation-type { background-color: #f3e5f5; color: #4a148c; }
                            .other-type { background-color: #f5f5f5; color: #424242; }
                            .footer { 
                                margin-top: auto;
                                position: relative;
                                z-index: 1;
                            }
                            .signatures {
                                display: flex;
                                justify-content: space-between;
                                margin-bottom: 4mm;
                            }
                            .signature-block { 
                                width: 45%;
                            }
                            .signature-line {
                                margin-top: 12mm;
                                border-top: 1.5px solid #999;
                                padding-top: 1.2mm;
                                font-size: 3mm;
                                color: #666;
                                text-align: center;
                            }
                            .signature-label {
                                text-align: center;
                                font-weight: bold;
                                font-size: 3mm;
                            }
                            .footer-content {
                                text-align: center;
                                margin-top: 4mm;
                                border-top: 1.5px solid #eaeaea;
                                padding-top: 2.5mm;
                            }
                            .thank-you { 
                                font-weight: bold;
                                margin-bottom: 2mm;
                                font-size: 3.3mm;
                                color: #333;
                                text-align: center;
                            }
                            .contact-info {
                                font-size: 2.8mm;
                                color: #666;
                                margin-bottom: 2mm;
                                display: flex;
                                justify-content: center;
                                gap: 3.5mm;
                            }
                            .contact-item {
                                display: flex;
                                align-items: center;
                                gap: 1.2mm;
                            }
                            .icons {
                                display: inline-block;
                                width: 3.3mm;
                                height: 3.3mm;
                                text-align: center;
                                line-height: 3.3mm;
                            }
                            .stamp {
                                position: absolute;
                                bottom: 40mm;
                                right: 20mm;
                                width: 18mm;
                                height: 18mm;
                                border: 1.5px dashed #999;
                                border-radius: 50%;
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                color: #999;
                                font-size: 3mm;
                                transform: rotate(-15deg);
                                opacity: 0.7;
                            }
                            .stamp-inner {
                                border: 1.5px solid #999;
                                width: 14mm;
                                height: 14mm;
                                border-radius: 50%;
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                text-align: center;
                            }
                            .software-info {
                                text-align: center;
                                font-size: 2.8mm;
                                color: #777;
                                margin-top: 3mm;
                                padding: 4mm;
                                border-top: 1px dashed #ddd;
                                width: 100%;
                                position: absolute;
                                bottom: 0;
                                left: 0;
                                right: 0;
                                background-color: #f9f9f9;
                                border-radius: 0 0 6mm 6mm;
                            }
                            .website-link {
                                color: #0000EE;
                                text-decoration: underline;
                            }
                        }
                    </style>
                </head>
                <body>
                    <div class="print-container">
                        <!-- First Receipt -->
                        <div class="receipt-wrapper">
                            <div class="receipt">
                                <div class="header">
                                    <div class="logo">مكتب المحاماة</div>
                                    <div class="logo-subtitle">للاستشارات القانونية والمحاماة</div>
                                    <div class="title">إيصال دفع رسمي</div>
                                    <div class="receipt-info">
                                        <div class="receipt-number-container">
                                            <div class="receipt-number-label">رقم الإيصال:   <span class="receipt-number">${payment.id}</span></div>
                                        </div>
                                        <div class="date-container">
                                            <div class="date-label">التاريخ:   <span class="date-value">${formattedDate}</span></div>
                                        </div>
                                    </div>
                                </div>
                                
                                <div class="details-container">
                                    <div class="details">
                                        <div class="section-title">تفاصيل العميل</div>
                                        <div class="detail-row">
                                            <span class="detail-label">اسم العميل:</span> 
                                            <span class="description-value">${payment.client_name || 'غير محدد'}</span>
                                        </div>
                                        ${payment.case_number ? `
                                        <div class="detail-row">
                                            <span class="detail-label">رقم القضية:</span> 
                                            <span class="description-value">${payment.case_number}</span>
                                        </div>` : ''}
                                        <div class="detail-row">
                                            <span class="detail-label">نوع الدفعة:</span> 
                                            <div class="payment-type ${
                                                payment.type === 'case' ? 'case-type' : 
                                                payment.type === 'consultation' ? 'consultation-type' : 'other-type'
                                            }">
                                                ${payment.type === 'case' ? 'قضية' : 
                                                payment.type === 'consultation' ? 'استشارة' : 'أخرى'}
                                            </div>
                                        </div>
                                        ${payment.description ? `
                                        <div class="detail-row">
                                            <span class="detail-label">الوصف:</span> 
                                            <span class="description-value">${payment.description}</span>
                                        </div>` : ''}
                                    </div>
                                </div>
                                
                                <div class="amount-section">
                                    <div class="amount-label">المبلغ المدفوع</div>
                                    <div class="amount-value">${payment.amount.toLocaleString('ar-EG')} دينار عراقي</div>
                                    <div class="amount-in-words">فقط ${convertNumberToArabicWords(payment.amount)} دينار عراقي لا غير</div>
                                </div>

                                <div class="stamp">
                                    <div class="stamp-inner">ختم المكتب</div>
                                </div>

                                <div class="footer">
                                    <div class="signatures">
                                        <div class="signature-block">
                                            <div class="signature-label">توقيع المستلم</div>
                                            <div class="signature-line">المستلم</div>
                                        </div>
                                        <div class="signature-block">
                                            <div class="signature-label">توقيع العميل</div>
                                            <div class="signature-line">العميل</div>
                                        </div>
                                    </div>
                                    
                                    <div class="footer-content">
                                        <div class="thank-you">شكراً لتعاملكم معنا</div>
                                        <div class="contact-info">
                                            <div class="contact-item">
                                                <span class="icons">📞</span>
                                                <span>07XXXXXXXXX</span>
                                            </div>
                                            <div class="contact-item">
                                                <span class="icons">📧</span>
                                                <span>office@lawyer.com</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                <div class="software-info">
                                    تم إنشاء هذا الإيصال باستخدام برنامج "المرجع برو"<br>
                                    <span class="website-link">www.AlmarjeaPro.com</span>
                                </div>
                            </div>
                        </div>
                        
                        
                    </div>
                    <script>
                        window.onload = function() {
                            setTimeout(function() {
                                window.print();
                            }, 500);
                        };
                    </script>
                </body>
                </html>
            `;
            
            // Create and open new window
            const printWindow = window.open('', '_blank');
            if (!printWindow) {
                Swal.fire({
                    title: 'تنبيه',
                    text: 'يرجى السماح بفتح النوافذ المنبثقة لتتمكن من الطباعة',
                    icon: 'warning'
                });
                return;
            }
            printWindow.document.write(receiptContent);
            printWindow.document.close();
            
        } catch (error) {
            logger.error("Error printing receipt:", error);
            Swal.fire({
                title: 'خطأ!',
                text: 'حدث خطأ أثناء طباعة الإيصال',
                icon: 'error'
            });
        }
    };

    return (
        <Button
            type={showLabel ? "primary" : "text"}
            
            icon={<PrinterOutlined style={{ fontSize: showLabel ? '1rem' : '1.35rem' }} />}
            onClick={printReceipt}
            className={showLabel ? className : "text-green-600 hover:text-green-700 flex items-center justify-center"}
            title='طباعة الإيصال'
            style={!showLabel ? { padding: '2px', height: 'auto', lineHeight: 1 } : {}}
        >
            {showLabel && "طباعة الإيصال"}
        </Button>
    );
};

export default PaymentReceipt;